import { useState } from '@hookstate/core';
import { useCallback, useContext, useEffect, useMemo } from 'react';

import { Icon as BlueIcon } from '@blueprintjs/core';

import ReactDom from 'react-dom';
import ReactTooltip from 'react-tooltip';

import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  getUpdateRelations,
  patchUpdate,
  postCreateUpdate,
} from '@services/queries/updates';

import { IUpdateRawQueryList, UpdateType } from '@models/Update.model';

import { NavigationContext } from '@layout/navigation/navigation';
import { IProductCreateUpdate } from '@models/Product.model';
import { ITagRaw } from '@models/Tag.model';

import {
  UpdateStatusPriority,
  UpdateStatusType,
} from '@models/UpdateStatus.model';

import { useParams } from 'react-router-dom';

import { PulseLoader } from 'react-spinners';

import { useAppDispatch } from '@store/redux/store';
import { addUpdatedUpdate } from '@store/redux/features/updatesOptimistic';
import { trackInternalUpdatesCreation } from '@services/mixpanel/trackers/InternalUpdatesMetrics';
import {
  addBackupNewUpdate,
  addBackUpUpdatedUpdates,
} from '@store/redux/features/updatesOptimistic/updatesOptimisticSlice';
import { formatDate } from '@utils/index';
import { isDefined } from '@utils/isNonNullable';
import { Preview } from './components/Preview/Preview';

import { AffectedItems } from './components/AffectedItems/AffectedItems';

import { Details } from './components/Details/Details';
import { RelationshipType } from './components/RelationshipType/RelationshipType';

import {
  CreateUpdateContext,
  RelatesToOptions,
} from './CreateUpdatesDialog.query';
import { IDetailsFormData } from './components/Details/types';
import {
  Back,
  Body,
  ButtonsContainer,
  Close,
  Confirm,
  Container,
  Divider,
  Header,
  HelpToolTipDiv,
  HelpToolTipIconContainer,
  HelpToolTipTextDiv,
  Indicator,
  Item,
  Label,
  LabelText,
  Line,
  LoadingFilter,
  Overlay,
  Span,
  Tabs,
  TabsContainer,
  Title,
} from './CreateUpdatesDialog.styles';

interface ITabItem {
  name: string;
  isActive: boolean;
  display: 'none' | 'flex';
}

const TabItem = ({ display, isActive, name }: ITabItem) => {
  return (
    <Item>
      <Label>
        <div>
          <LabelText isActive={isActive}>{name}</LabelText>
        </div>
        <Span display={display}>
          <BlueIcon icon="tick-circle" color="#8F95B2" />
        </Span>
      </Label>

      <Indicator isActive={isActive} />
    </Item>
  );
};

interface ICreateUpdatesDialogRaw {
  open: boolean;
  onClose: () => void;
  tab?: 'relationship' | 'affected' | 'details';
  relatesToPrev?: RelatesToOptions;
  productPrev?: IProductCreateUpdate;
  edit?: {
    updatePrev: IUpdateRawQueryList;
  };
}

export const CreateUpdatesDialogRaw = ({
  open,
  onClose,
  relatesToPrev,
  tab,
  productPrev,
  edit,
}: ICreateUpdatesDialogRaw) => {
  const portalDiv = document.getElementById('portal');

  const params = useParams<{ equipmentId: string }>();

  const {
    relatesTo,
    selectedMenufacturers,
    selectedProducts,
    detailsIsValid,
    title,
    date,
    priority,
    description,
    searchManufacturer,
    searchProducts,
    setSelectedMenufacturers,
    setSelectedProducts,
    setProductManufacturerValue,
  } = useContext(CreateUpdateContext);
  const { studioId, updatesMtViewTime, studioName } =
    useContext(NavigationContext);

  const page = useState<'relationship' | 'affected' | 'details'>(
    'relationship',
  );
  const loadingCreate = useState<boolean>(false);

  const dispatch = useAppDispatch();

  const queryClient = useQueryClient();

  const relationsQuery = useQuery(
    ['updates', edit?.updatePrev.id, 'relations'],
    () => getUpdateRelations(edit?.updatePrev.id),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  );

  const previewAffects = useMemo(() => {
    if (relatesTo.value === 'manufacturers') {
      const equipmentsCount = selectedMenufacturers.reduce(
        (previousValue, currentValue) =>
          previousValue +
          (currentValue.equipmentsCount ? currentValue.equipmentsCount : 0),
        0,
      );

      return equipmentsCount;
    }

    if (relatesTo.value === 'products') {
      const equipmentsCount = selectedProducts.reduce(
        (previousValue, currentValue) =>
          previousValue +
          (currentValue.equipmentsCount ? currentValue.equipmentsCount : 0),
        0,
      );

      return equipmentsCount;
    }

    return 0;
  }, [relatesTo.value, selectedMenufacturers, selectedProducts]);

  const relatedTo = relatesTo.value;
  const relatedInfo =
    relatesTo.value === 'manufacturers'
      ? selectedMenufacturers.map(item => item.name)
      : selectedProducts.map(item => item.model);
  const numberOfAssets =
    relatesTo.value === 'manufacturers'
      ? selectedMenufacturers.length
      : selectedProducts.length;
  const updatePriority = priority.value;
  const dueDate = date.value;

  const createUpdateMutation = useMutation(postCreateUpdate, {
    onSuccess: async newUpdate => {
      dispatch(addBackupNewUpdate(newUpdate));

      onClose();

      detailsIsValid.set(false);
      title.set('');
      date.set(null);
      priority.set(UpdateStatusType.NEW);
      description.set('');
      setSelectedMenufacturers([]);
      setProductManufacturerValue(null);

      searchManufacturer.set('');
      searchProducts.set('');

      if (tab === 'affected' && relatesToPrev && productPrev) {
        page.set('affected');
        relatesTo.set(relatesToPrev);
        setSelectedProducts([productPrev]);
      } else {
        page.set('relationship');
        relatesTo.set(null);
        setSelectedProducts([]);
      }

      trackInternalUpdatesCreation(
        studioName,
        relatedTo,
        relatedInfo,
        numberOfAssets,
        dueDate,
        updatePriority,
      );

      queryClient.invalidateQueries(['studios', studioId, 'updateStatuses']);
      queryClient.invalidateQueries([
        'equipments',
        params.equipmentId,
        'updateStatuses',
      ]);
      queryClient.invalidateQueries(['updateStatuses']);
      queryClient.invalidateQueries(['updatesMetrics']);
    },
  });

  const patchUpdateMutation = useMutation(patchUpdate, {
    onSuccess: async newUpdate => {
      dispatch(addBackUpUpdatedUpdates(newUpdate));
      dispatch(addUpdatedUpdate({ newUpdate, updatesMtViewTime }));

      onClose();

      detailsIsValid.set(false);
      title.set('');
      date.set(null);
      priority.set(UpdateStatusType.NEW);
      description.set('');
      setSelectedMenufacturers([]);
      setProductManufacturerValue(null);

      searchManufacturer.set('');
      searchProducts.set('');

      if (tab === 'affected' && relatesToPrev && productPrev) {
        page.set('affected');
        relatesTo.set(relatesToPrev);
        setSelectedProducts([productPrev]);
      } else {
        page.set('relationship');
        relatesTo.set(null);
        setSelectedProducts([]);
      }

      queryClient.invalidateQueries(['studios', studioId, 'updateStatuses']);
      queryClient.invalidateQueries([
        'equipments',
        params.equipmentId,
        'updateStatuses',
      ]);
      queryClient.invalidateQueries(['updateStatuses']);
      queryClient.invalidateQueries(['updatesMetrics']);
    },
  });

  const ReturnButton = useCallback(() => {
    if (page.value === 'affected') page.set('relationship');
    if (page.value === 'details') page.set('affected');
  }, [page.value]);

  const NextButton = () => {
    if (page.value === 'relationship') {
      page.set('affected');
      return;
    }
    if (page.value === 'affected') {
      page.set('details');
      return;
    }
    if (page.value === 'details') {
      const type = () => {
        if (relatesTo.value === 'tags') return UpdateType.TAGS;
        if (relatesTo.value === 'manufacturers')
          return UpdateType.MANUFACTURERS;
        return UpdateType.PRODUCTS;
      };

      if (edit) {
        patchUpdateMutation.mutate({
          id: edit.updatePrev.id,
          description: description.value,
          manufacturersIds:
            relatesTo.value === 'manufacturers'
              ? selectedMenufacturers.map(item => item.id)
              : [],
          productsIds:
            relatesTo.value === 'products'
              ? selectedProducts.map(item => item.id)
              : [],
          originalMessage: '',
          subject: title.value,
          tagsIds: [],
          dataDate: new Date(),
          dueDate: new Date(date.value as string),
          type: type(),
          priority:
            priority.get() === UpdateStatusType.NEW
              ? undefined
              : (priority.get() as UpdateStatusPriority),
          usType:
            priority.get() === UpdateStatusType.NEW
              ? UpdateStatusType.NEW
              : UpdateStatusType.PRIORITIZED,
        });
      } else {
        createUpdateMutation.mutate({
          description: description.value,
          manufacturersIds:
            relatesTo.value === 'manufacturers'
              ? selectedMenufacturers.map(item => item.id)
              : [],
          productsIds:
            relatesTo.value === 'products'
              ? selectedProducts.map(item => item.id)
              : [],
          originalMessage: '',
          subject: title.value,
          tagsIds: [],
          dataDate: new Date(),
          dueDate: new Date(date.value as string),
          type: type(),
          priority:
            priority.get() === UpdateStatusType.NEW
              ? undefined
              : (priority.get() as UpdateStatusPriority),
          usType:
            priority.get() === UpdateStatusType.NEW
              ? UpdateStatusType.NEW
              : UpdateStatusType.PRIORITIZED,
        });
      }
    }
  };

  const nextButtonDisabled = useMemo(() => {
    if (page.value === 'relationship') {
      if (!relatesTo.get()) return true;
    }
    if (page.value === 'affected') {
      if (relatesTo.get() === 'manufacturers') {
        if (selectedMenufacturers.length === 0) return true;
      }
      if (relatesTo.get() === 'products') {
        if (selectedProducts.length === 0) return true;
      }
    }
    if (page.value === 'details') {
      if (!detailsIsValid.get()) return true;
      if (createUpdateMutation.isLoading) return true;
    }

    return false;
  }, [
    page.value,
    relatesTo.get(),
    selectedMenufacturers,
    selectedProducts,
    detailsIsValid.get(),
    createUpdateMutation.isLoading,
  ]);

  const previewTagsCount = useMemo(() => {
    const tagsArr =
      relatesTo.value === 'manufacturers'
        ? selectedMenufacturers.map(item => item.tags).flat()
        : selectedProducts.map(item => item.tags).flat();

    const tags: ITagRaw[] = [];
    tagsArr.forEach(tag => {
      if (tag && !tags.find(item => item.name === tag.name)) tags.push(tag);
    });

    return tags.length - 1;
  }, [relatesTo.value, selectedMenufacturers, selectedProducts]);

  const previewFirstTag = useMemo(() => {
    if (relatesTo.value === 'manufacturers') {
      const findFisrtManufacturerWithTag = selectedMenufacturers.find(
        item => item.tags && item.tags.length > 0,
      );

      if (findFisrtManufacturerWithTag?.tags)
        return findFisrtManufacturerWithTag?.tags[0];
    }

    if (relatesTo.value === 'products') {
      const findFisrtProductWithTag = selectedProducts.find(
        item => item.tags && item.tags.length > 0,
      );

      if (findFisrtProductWithTag?.tags)
        return findFisrtProductWithTag?.tags[0];
    }

    return undefined;
  }, [relatesTo.value, selectedMenufacturers, selectedProducts]);

  const ReturnDisplay = () => {
    if (page.value === 'relationship') return 'none';
    return 'flex';
  };

  const buttonText = () => {
    if (page.value === 'details') {
      if (edit) return 'Save Update';
      return 'Create Update';
    }

    return 'Next';
  };

  const ToolTipText = () => {
    if (page.value === 'affected')
      return (
        <>
          Before adding details, we need to know what <br />
          the updates are related to. To start adding <br />
          Affected Items choose “Add Manufacturer” or <br />
          “Add Product” to add an item.
        </>
      );
    if (page.value === 'details')
      return (
        <>
          All fields above are required before <br />
          confirming the creation of your update. <br />
          Hint: Attach files by using the “Attachments” <br />
          button found in the description box.
        </>
      );
    if (page.value === 'relationship')
      return (
        <>
          Select the first options if this update is related <br />
          to a product or a manufacturer. However <br />
          using the second option, you can add an <br />
          update for the assets with a tag, perhaps a<br />
          camera kit needing to change studios.
        </>
      );
    return '';
  };

  const CancelButton = () => {
    if (tab === 'affected' && relatesToPrev && productPrev) {
      page.set('affected');
      relatesTo.set(relatesToPrev);
      setSelectedProducts([productPrev]);
    } else {
      page.set('relationship');
      relatesTo.set(null);
      setSelectedProducts([]);
    }

    setSelectedMenufacturers([]);
    title.set('');
    description.set('');
    date.set(null);
    priority.set(UpdateStatusType.NEW);
    detailsIsValid.set(false);

    setProductManufacturerValue(null);
    onClose();
  };
  function syncUpdateDetails(newValues: Partial<IDetailsFormData>): void {
    if (isDefined(newValues.date)) {
      date.set(formatDate(newValues.date));
    }
    if (isDefined(newValues.priority)) {
      priority.set(newValues.priority);
    }
    if (isDefined(newValues.description)) {
      description.set(newValues.description);
    }
    if (isDefined(newValues.title)) title.set(newValues.title);
  }

  useEffect(() => {
    if (edit) {
      if (!relatesTo.value) relatesTo.set(edit.updatePrev.type as any);
      if (!title.value) title.set(edit.updatePrev.subject);
      if (!description.value) description.set(edit.updatePrev.description);

      if (edit.updatePrev.usType === UpdateStatusType.NEW) {
        priority.set(UpdateStatusType.NEW);
      } else {
        priority.set(edit.updatePrev.priority);
      }

      detailsIsValid.set(true);
      if (edit.updatePrev.date && !date.value)
        date.set(edit.updatePrev.date.toString());
      if (relationsQuery.data) {
        if (edit.updatePrev.type === 'products') {
          if (selectedProducts.length === 0) {
            setSelectedProducts(relationsQuery.data.products);
          }
        } else if (selectedMenufacturers.length === 0) {
          setSelectedMenufacturers(relationsQuery.data.manufacturers);
        }
      }

      return;
    }

    if (tab === 'affected' && relatesToPrev && productPrev) {
      page.set('affected');
      relatesTo.set(relatesToPrev);
      if (selectedProducts.length === 0) setSelectedProducts([productPrev]);
    }
  }, [tab, relatesToPrev, productPrev, edit, relationsQuery.data]);

  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            {loadingCreate.value && (
              <LoadingFilter>
                <PulseLoader color="#1371fe" />
              </LoadingFilter>
            )}
            <Header>
              <Title>Create Update</Title>
              <Close onClick={onClose}>
                <BlueIcon icon="cross" color="#8F95B2" />
              </Close>
            </Header>
            <Divider />

            <Preview
              priority={priority.get()}
              subject={title.get()}
              affects={previewAffects}
              firstTag={previewFirstTag}
              tagsCount={previewTagsCount}
              date={date.value}
            />

            <ReactTooltip
              className="tooltip"
              id="previewTooltip"
              effect="solid"
              multiline
            >
              <HelpToolTipTextDiv>
                This is a summary of your update, as
                <br />
                you fill in information it will be updated
              </HelpToolTipTextDiv>
            </ReactTooltip>

            <Divider />

            <TabsContainer>
              <Tabs>
                <TabItem
                  name="1. Relationship Type"
                  isActive={page.get() === 'relationship'}
                  display={page.get() !== 'relationship' ? 'flex' : 'none'}
                />
                <TabItem
                  name="2. Affected Items"
                  isActive={page.get() === 'affected'}
                  display={page.get() === 'details' ? 'flex' : 'none'}
                />
                <TabItem
                  name="3. Details"
                  isActive={page.get() === 'details'}
                  display="none"
                />
              </Tabs>
              <Back />
            </TabsContainer>

            <Body show={page.get() === 'relationship'}>
              <RelationshipType />
            </Body>

            <Body show={page.get() === 'affected'}>
              <AffectedItems />
            </Body>

            <Body show={page.get() === 'details'}>
              <Details
                update={edit?.updatePrev}
                mode={edit ? 'editing' : 'create'}
                broadcast={syncUpdateDetails}
                onValidChange={detailsIsValid.set}
              />
            </Body>

            <Line>
              <HelpToolTipDiv data-tip data-for="helpTooltip" display>
                <HelpToolTipIconContainer>
                  <BlueIcon icon="help" color="#8F95B2" />
                </HelpToolTipIconContainer>

                <LabelText className="help">Help</LabelText>
              </HelpToolTipDiv>

              <ButtonsContainer>
                <Confirm
                  type="button"
                  className="return"
                  onClick={() => {
                    CancelButton();
                  }}
                  display="flex"
                >
                  Cancel
                </Confirm>
                <Confirm
                  type="button"
                  className="return"
                  onClick={ReturnButton}
                  display={ReturnDisplay()}
                >
                  Return
                </Confirm>
                <Confirm
                  type="button"
                  onClick={NextButton}
                  disabled={nextButtonDisabled}
                >
                  {buttonText()}
                </Confirm>
              </ButtonsContainer>

              <ReactTooltip
                className="tooltip"
                id="helpTooltip"
                effect="solid"
                multiline
              >
                <HelpToolTipTextDiv>{ToolTipText()}</HelpToolTipTextDiv>
              </ReactTooltip>
            </Line>
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
